<template>
    <div
             class="bg-white break-inside-avoid">
        <!-- Show Loading component till the company is loaded -->
        <div    v-if="!loaded"
                class="col-span-5 grid grid-cols-2 md:grid-cols-5">
            <div class="mx-4 col-span-2 md:col-span-5 ">
                <loading class="w-1/12 h-4 my-2"></loading>
                <loading class="w-2/6 h-4 my-2"></loading>
            </div>
        </div>
        <!-- otherwise make sure we show the company -->
        <div v-else-if="shouldShow && !demoHide">
            <!-- Company has a site & we should just display normal info -->
            <a      v-if="company.has_site"
                    :href="`/${company['id']}`"
                    :class="gridCols"
                    class="grid grid-cols-2 plain-link">
                <!-- Version for larger screens with offsets -->
                <div    class="hidden md:block col-span-2 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        :style="{'margin-left' : calculateTieringOffset(company)}">
                    <div class="text-base font-semibold italic text-gray-700 my-2 ">
                        {{ companyName(company) }}
                        <div
                          v-if="company.suspended"
                          class="inline-flex justify-center items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800 md:mt-2 lg:mt-0">
                            Site Inactive
                        </div>
                    </div>
                    <div :style="{'margin-left' : toLargestOffset(company)}">
                        <wizard-status  v-if="!company.siteActive"
                                        :status="company.wizardSummary"
                                        :company="company.id"></wizard-status>

                        <point-status   v-else
                                        :info="company.pointSummary"
                        ></point-status>
                    </div>
                </div>
                <!-- Version for smaller screens with offset -->
                <div    class="block md:hidden col-span-2 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div class="text-base font-semibold italic text-gray-700">
                        {{ companyName(company) }}
                    </div>
                    <div>
                        <wizard-status  v-if="!company.siteActive"
                                        :company="company.id"
                                        :status="company.wizardSummary"
                        ></wizard-status>

                        <point-status   v-else
                                        :info="company.pointSummary"
                        ></point-status>
                    </div>
                </div>
                <div v-if="company.has_site" class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center justify-around md:justify-start">
                    <div class="rounded-lg py-1 px-2 md:p-0">
                        <a      :href="alarmsLink(company, 'threshold')"
                                class="plain-link">
                            <div class="p-2 md:p-4">
                                <div class="flex flex-row items-center my-1 mr-8">
                                    <span class="lnr icon-coolant-temperature text-blue-500"></span>
                                    <div class="pl-3 flex flex-col items-begin">
                                        <span   class="text-2xl"
                                                :class="[company['alarmSummary'].threshold.active > 0 ? 'font-medium text-gray-600' : 'font-base text-gray-400']">
                                            {{ company['alarmSummary'].threshold.active }}
                                            <span v-if="company.alarmSummary.threshold.snoozed > 0"
                                                  class="hidden md:inline text-sm leading-5 font-medium text-gray-500">
                                                +{{ company.alarmSummary.threshold.snoozed }} snoozed
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
                <div v-if="company.has_site" class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center justify-around md:justify-start">
                    <div v-if="loaded" class="rounded-lg py-1 px-2 md:px-0 md:my-0">
                        <a      :href="alarmsLink(company, 'offline')"
                                class="plain-link">
                            <div class="p-2 md:p-4">
                                <!-- Content goes here -->
                                <div class="flex flex-row items-center my-1 mr-16">
                                    <span class="lnr icon-cloud-crossed text-blue-500"></span>
                                    <div class="pl-3 flex flex-col items-begin">
                                        <span class="text-2xl"
                                              :class="[company['alarmSummary'].offline.active > 0 ? 'font-medium text-gray-600' : 'font-base text-gray-400']">
                                            {{ company.alarmSummary.offline.active }}
                                            <span   v-if="company.alarmSummary.offline.snoozed > 0"
                                                    class="hidden md:inline text-sm leading-5 font-medium text-gray-500">
                                                +{{ company.alarmSummary.offline.snoozed }} snoozed
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>

                </div>
                <div    v-if="company.has_site && canViewSnapshot"
                        class="hidden md:block px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div class="h-full">
                        <snapshot-issues    :company="company['id']"
                                            :issues="company.snapshotSummary"
                                            :snap-enrolled="company.snapEnrolled === 1"
                        ></snapshot-issues>
                    </div>
                </div>
            </a>
            <!-- Company does not have a site & so we should just display the company name -->
            <!-- Hide On Mobile because the parent doesn't make sense without the tiered offsets. -->
            <a      v-else
                    :href="`/${company['id']}`"
                    class="hidden md:block grid grid-cols-2 md:grid-cols-5 plain-link">
                <div    class="col-span-5 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        :style="{'margin-left' : calculateTieringOffset(company)}">
                    <div class="text-base font-semibold text-gray-700 my-2">
                        {{ companyName(company) }}
                    </div>
                </div>
            </a>
        </div>
    </div>
</template>

<script>
import AlarmStatus from "@/components/EnterpriseDashboard/AlarmStatus"
import PointStatus from '@/components/EnterpriseDashboard/PointStatus'
import SnapshotIssues from "@/components/EnterpriseDashboard/SnapshotIssues"
import WizardStatus from '@/components/EnterpriseDashboard/WizardStatus'

import { iconNamesMixin } from "@/mixins/IconNamesMixin";


export default {
    name: "EnterpriseDashboardRow",
    mixins: [iconNamesMixin],
    components: {AlarmStatus, PointStatus, SnapshotIssues, WizardStatus},
    props: {
        companyId: String,
        company: Object,
        loaded: Boolean,
        largestDepth: Number,
        canViewSnapshot: Boolean,
        isSonicu: Boolean,
        shouldShow: Boolean,
        demoHide: {
            type: Boolean,
            default: false,
        },
        gridCols: {
            type: String,
            default: 'md:grid-cols-5'
        }
    },
    data() {
        return {
        }
    },
    created() {
    },
    mounted() {
    },
    methods: {
        alarmsLink(company, alarmType) {
            let url = `/${company.id}/alarms?`
            url += alarmType === 'threshold' ? 'showThreshold=true&showOffline=false&showValidation=false' : 'showThreshold=false&showOffline=true&showValidation=false'
            return url;
        },
        companyName(company) {
            // Provide a company name for non-sonicu users or company name (id) for sonicu users
            let name = ''
            if (this.isSonicu) {
                name = `${company['name']} (${company['id']})`
            }
            else {
                name = `${company['name']}`
            }
            return name
        },
        calculateTieringOffset(company) {
            return (company.depth * 3) + 'em'
        },
        toLargestOffset(company) {
            return ((this.largestDepth - company.depth) * 3) + 'em'
        },
    },
    computed: {
        // TODO: Convert largestDepth to parent code
    },
    watch: {
    },
    beforeUnmount() {
    },
}

</script>
